/**
 * Which Asset Management routes own their UDA scope.
 *
 * The Asset Management dashboard picks its UDA in the page's own Filters panel
 * (see `components/AssetFilters.tsx`), and that selection rides on every asset
 * request as `uda_id`. The global header's UDA dropdown is a different thing —
 * a tenant switch that re-issues the JWT — so leaving it live on this screen
 * gives the user two controls that disagree about what they are looking at.
 *
 * Header.tsx disables its own dropdown for the paths matched here, the same way
 * it already does for the Employee Management screens (`isUdaScopedRoute`).
 *
 * The registers below are listed one by one rather than matched as "anything
 * under the module". The MASTERS (categories, asset type, action types,
 * ownership type, mode of acquisition, category mapping) are deliberately NOT
 * here: those tables carry no UDA scoping server-side — AssetTypeMdl,
 * AssetActionTypesMdl, ModeOfAcquisitionMdl and OwnershipTypeMdl have no uda_id
 * at all, and AssetCategoryMdl's reads are explicitly not filtered on it — so a
 * picker there would be a control that changes nothing. They keep the header
 * switcher, which still means something to them (creating a master category
 * stamps uda_id from the JWT).
 */
const ASSET_MANAGEMENT_BASE = '/maud-erp/asset-management';

/**
 * Sub-paths that carry their own picker. Each of these screens sends `uda_id` on
 * every request, read server-side by `resolveUdaId`.
 */
const SCOPED_SUBROUTES = [
  'work-orders',
  'disposals',
  'valuations',
  'inspections',
  'encroachments',
  'verify',
  'depreciation',
  'geo-import',
  'data-quality',
];

export function isAssetUdaScopedRoute(pathname: string): boolean {
  const p = (pathname || '').toLowerCase().replace(/\/+$/, '');
  const base = p.indexOf(ASSET_MANAGEMENT_BASE);
  if (base === -1) return false;

  // The dashboard is the module's index route, so it is the bare base path.
  const rest = p.slice(base + ASSET_MANAGEMENT_BASE.length).replace(/^\//, '');
  if (rest === '') return true;

  // 'inspections/field' and the like are covered by the first segment.
  const first = rest.split('/')[0];
  return SCOPED_SUBROUTES.includes(first);
}
